import { Text, Stack } from '@gazin/ecommerce-ui'
import { PaginationItem } from '../PaginationItem'

export const FIRST_PAGE = 1

interface PaginationGroupItemsProps {
  currentPage: number
  lastPage: number
  onNavigate?: (page: number) => void
}

function generatePages(currentPage: number, lastPage: number) {
  const isLastPage = currentPage === lastPage
  const hasOnlyOnePage = currentPage === FIRST_PAGE && isLastPage
  const belongsToFirstGroupOfPages = currentPage <= 4
  const belongsToLastGroupOfPages =
    !belongsToFirstGroupOfPages && currentPage > lastPage - 4
  const penultimatePage = lastPage - 1
  const previousPage = currentPage - 1

  let previousPages: number[] = []
  let nextPages: number[] = []

  switch (true) {
    case hasOnlyOnePage:
      break
    case belongsToFirstGroupOfPages:
      previousPages = generatePagesInRange(
        FIRST_PAGE,
        Math.max(previousPage, FIRST_PAGE)
      )
      if (!isLastPage) {
        nextPages = generatePagesInRange(
          currentPage,
          Math.min(5, penultimatePage)
        )
      }
      break
    case belongsToLastGroupOfPages:
      previousPages = generatePagesInRange(
        Math.max(lastPage - 5, FIRST_PAGE),
        previousPage
      )
      nextPages = generatePagesInRange(
        Math.min(currentPage, penultimatePage),
        penultimatePage
      )
      break
    default:
      previousPages = generatePagesInRange(
        Math.max(currentPage - 3, FIRST_PAGE),
        previousPage
      )
      nextPages = generatePagesInRange(
        currentPage,
        Math.min(currentPage + 2, penultimatePage)
      )
      break
  }

  return { previousPages, nextPages }
}

function generatePagesInRange(from: number, to: number) {
  return [...new Array(to - from)]
    .map((_, index) => from + index + 1)
    .filter((page) => page > 0)
}

export const PaginationGroupItems = ({
  currentPage,
  lastPage,
  onNavigate,
}: PaginationGroupItemsProps) => {
  const { previousPages, nextPages } = generatePages(currentPage, lastPage)

  const showFirstPageEllipsis =
    previousPages.length > 0 && previousPages[0] - 1 !== FIRST_PAGE
  const showLastPageEllipsis =
    nextPages.length > 0 && nextPages[nextPages.length - 1] + 1 !== lastPage

  return (
    <Stack direction="row" spacing="2">
      {currentPage !== FIRST_PAGE && (
        <>
          <PaginationItem page={FIRST_PAGE} onNavigate={onNavigate} />
          {showFirstPageEllipsis && (
            <Text textAlign={'center'} width="8" color="#646981">
              ...
            </Text>
          )}
        </>
      )}
      {previousPages.length > 0 &&
        previousPages.map((page) => (
          <PaginationItem key={page} page={page} onNavigate={onNavigate} />
        ))}
      <PaginationItem isCurrent page={currentPage} />
      {nextPages.length > 0 &&
        nextPages.map((page) => (
          <PaginationItem key={page} page={page} onNavigate={onNavigate} />
        ))}
      {currentPage !== lastPage && (
        <>
          {showLastPageEllipsis && (
            <Text textAlign="center" width="8" color="#646981">
              ...
            </Text>
          )}
          <PaginationItem page={lastPage} onNavigate={onNavigate} />
        </>
      )}
    </Stack>
  )
}
